import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { WeatherSuit, type WeatherSuit as TWeatherSuit } from '@hewey/shared'

// Today's forecast, distilled to the weatherSuit tags it favours plus a
// friendly headline. Open-Meteo (https://open-meteo.com) is a free, keyless,
// CORS-enabled forecast API, so we call it straight from the browser and parse
// the response with Zod — no server proxy needed (unlike the Nominatim geocode,
// which needs a User-Agent the browser can't set).
export type TodayWeather = {
	// The weatherSuit tags today's conditions call for. An activity belongs in
	// the row if its own suits overlap these (or it's tagged `any`).
	suits: TWeatherSuit[]
	headline: string
	detail: string
}

const TodayWeatherSchema = z.object({
	suits: z.array(WeatherSuit),
	headline: z.string(),
	detail: z.string(),
})

// One forecast per rounded location, kept for an hour. Spares Open-Meteo a
// request on every Explore visit and makes the weather row appear instantly on
// return. Validated on read like any other localStorage value.
const CACHE_KEY = 'hewey.activities.weather'
const ONE_HOUR_MS = 3_600_000

const CacheSchema = z.object({
	key: z.string(),
	ts: z.number(),
	weather: TodayWeatherSchema,
})

const readCache = (key: string): TodayWeather | null => {
	try {
		const raw = localStorage.getItem(CACHE_KEY)
		if (!raw) return null
		const parsed = CacheSchema.safeParse(JSON.parse(raw))
		if (!parsed.success) return null
		if (parsed.data.key !== key) return null
		if (Date.now() - parsed.data.ts > ONE_HOUR_MS) return null
		return parsed.data.weather
	} catch {
		return null
	}
}

const writeCache = (key: string, weather: TodayWeather): void => {
	try {
		localStorage.setItem(
			CACHE_KEY,
			JSON.stringify({ key, ts: Date.now(), weather }),
		)
	} catch {}
}

const OpenMeteoSchema = z.object({
	daily: z.object({
		weather_code: z.array(z.number()),
		temperature_2m_max: z.array(z.number()),
		precipitation_probability_max: z.array(z.number().nullable()),
	}),
})

// WMO weather codes: 51+ is some form of precipitation; 71–77 / 85–86 is snow.
const isSnow = (code: number): boolean =>
	(code >= 71 && code <= 77) || code === 85 || code === 86

const deriveTodayWeather = (
	code: number,
	tempMax: number,
	precipProb: number,
): TodayWeather => {
	const wet = code >= 51 || precipProb >= 50
	const suits: TWeatherSuit[] = [wet ? 'rainy_ok' : 'dry']
	if (tempMax >= 20) suits.push('hot')
	if (tempMax <= 8) suits.push('cold')

	const { headline, emoji } = isSnow(code)
		? { headline: 'Cosy snow-day ideas', emoji: '🌨️' }
		: wet
			? { headline: 'Rainy-day ideas', emoji: '☔' }
			: tempMax >= 20
				? { headline: 'Made for the sunshine', emoji: '☀️' }
				: tempMax <= 8
					? { headline: 'Wrap-up-warm ideas', emoji: '🧥' }
					: { headline: 'Good for today', emoji: '⛅' }

	return { suits, headline, detail: `${emoji} ${Math.round(tempMax)}°C` }
}

const fetchTodayWeather = async (
	lat: number,
	lng: number,
): Promise<TodayWeather | null> => {
	const url =
		`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
		'&daily=weather_code,temperature_2m_max,precipitation_probability_max' +
		'&timezone=auto&forecast_days=1'
	const res = await fetch(url)
	if (!res.ok) return null
	const data = OpenMeteoSchema.parse(await res.json())
	const code = data.daily.weather_code[0]
	const tempMax = data.daily.temperature_2m_max[0]
	if (code === undefined || tempMax === undefined) return null
	return deriveTodayWeather(
		code,
		tempMax,
		data.daily.precipitation_probability_max[0] ?? 0,
	)
}

// Today's weather for the given origin, served from the hourly cache when warm
// and fetched otherwise. Best-effort: any failure (offline, parse error, API
// down) leaves it null and the weather row simply doesn't appear. Coordinates
// are rounded into the key so tiny GPS jitter neither re-fetches nor misses the
// cache.
export const useTodayWeather = (
	origin: { lat: number; lng: number } | null,
): TodayWeather | null => {
	const [weather, setWeather] = useState<TodayWeather | null>(null)
	const handledKey = useRef<string | null>(null)

	useEffect(() => {
		if (!origin) return
		const key = `${origin.lat.toFixed(2)},${origin.lng.toFixed(2)}`
		if (handledKey.current === key) return
		handledKey.current = key

		const cached = readCache(key)
		if (cached) {
			setWeather(cached)
			return
		}

		let cancelled = false
		fetchTodayWeather(origin.lat, origin.lng)
			.then((w) => {
				if (cancelled || !w) return
				writeCache(key, w)
				setWeather(w)
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [origin])

	return weather
}
